import React, {useEffect, useState, useImperativeHandle, useMemo} from "react";
import {Tab, Menu} from "semantic-ui-react";
import insurance_types, {non_custom_insurance_types} from "constants/insurance_types";
import CoverageTypeSection from "./CoverageTypeSection";
import AddIcon from "@mui/icons-material/Add";
import {Button, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography} from "@mui/material";
import { SubtitleInline } from "../Typography";

export default React.forwardRef(({
  open=false,
  isEdit=false,
  coverages,
  clientCoverages=[],
  classCoverages=[],
  clientMode,
  showHelperText=false,
  vendorCoverages=[],
  classifications = [],
  descriptionOfOperationValidation,
  classificationIDKey,
  classificationID,
  refresh=0
}, ref) => {

  const [activeIndex, setActiveIndex] = useState(undefined)
  const [coverageData, setCoverageData] = useState(getInitialCoverageData())
  const [descriptionOfOperation, setDescriptionOfOperation] = useState(descriptionOfOperationValidation)
  const [references, setReferences] = useState({})

  useImperativeHandle(ref, () => ({
    getCoverageData: () => coverageData,
    getDescriptionOfOperation: () => (descriptionOfOperation || "").trim(),
  }), [coverageData, descriptionOfOperation]);

  useEffect(() => {
    resetCoverageData(isEdit)
  }, [])

  useEffect(() => {
    if(!open && !!isEdit) {
      setCoverageData(getInitialCoverageData())
    }
  }, [open])

  useMemo(() => {
    if(!isEdit && !!refresh) {
      resetCoverageData()
    }
  }, [refresh])

  function getInitialCoverageData() {
    return non_custom_insurance_types.map((insuranceType, index) => ({
      insuranceType,
      index,
      ...(
        (classificationIDKey && classificationID)
          ? {[classificationIDKey]: classificationID}
          : {}
      )
    }))
  }

  function getInsuranceListByType(insuranceList=[], insuranceType, isFind=false) {
    const method = isFind ? "find" : "filter"
    return insuranceList[method](oneCoverage => oneCoverage.insuranceType === insuranceType)
  }

  function resetCoverageData(shouldResetReference = false) {
    if(coverages?.length) {
      const newReferences = {}
      const newData = getInitialCoverageData()
      coverages.forEach((coverageRequirement, coverageIndex) => {
        const {insuranceType} = coverageRequirement
        const index = non_custom_insurance_types.indexOf(insuranceType)
        const coverage = {...coverageRequirement, index: coverageIndex, insuranceType}
        if(index === -1) {
          newData.push(coverage)
        } else {
          newData[index] = coverage
        }
        if(shouldResetReference) {
          newReferences[coverageIndex] = {}
          //TODO: CODE CLEANUP
          //CHECK RELATION AND USAGE FOR clientCoverages, classCoverages (related with categories) and vendorCoverages
          newReferences[coverageIndex]["clientCoverage"] = getInsuranceListByType(clientCoverages, insuranceType, true)
          newReferences[coverageIndex]["classCoverage"] = getInsuranceListByType(classCoverages, insuranceType, true)
          newReferences[coverageIndex]["vendorCoverage"] = getInsuranceListByType(vendorCoverages, insuranceType)
            ?.map(oneCoverage => {
              if (oneCoverage[classificationIDKey]) {
                const vendorClassification = classifications.find(
                  oneClassification => oneClassification.id === oneCoverage[classificationIDKey]
                )
                return {
                  ...oneCoverage,
                  classificationName: vendorClassification?.name || "Vendor Default"
                }
              }
            })
        }
      })
      setCoverageData(newData)
      shouldResetReference && setReferences(newReferences)
    }
  }

  function handleAddInsuranceType () {
    const insuranceType = 'CUSTOM'
    let index = coverageData.length;
    let clientCoverage = {}
    setCoverageData([
      ...coverageData,
      {...clientCoverage, insuranceType, index}
    ])
    setActiveIndex(index)
  }

  function onUpdate ({ id = null, name, value, index: updateIndex }) {
    const updated = coverageData.map(oneCoverage => {
      if(oneCoverage.index === updateIndex) {
        return {
          ...oneCoverage,
          [name]: value,
        }
      }
      return oneCoverage
    })
    setCoverageData(updated)
  }

  function onDeleteTab(index) {
    const update = coverageData.filter((_, i) => i !== index).map(oneCoverage => ({
    ...oneCoverage,
    index: oneCoverage.index > index ? oneCoverage.index - 1 : oneCoverage.index
    }));
    setCoverageData(update);
    setActiveIndex(--index);
    }

  function getPanes() {
    let panes = Array(non_custom_insurance_types.length).fill({});
    coverageData.forEach((coverageRequirement, tabIndex) => {
      const {insuranceType, index: coverageIndex} = coverageRequirement
      const index = non_custom_insurance_types.indexOf(insuranceType)

      const tabClassName = `coverage-tab-${coverageRequirement.insuranceType.toLowerCase()}`;
      const pane = {
        menuItem: (
          <Menu.Item key={tabIndex} style={{alignSelf: "stretch"}} className={tabClassName}>
            {(index === -1 &&coverageRequirement.name) ? String(coverageRequirement.name).substring(0,3).toUpperCase() : coverageRequirement.insuranceType}
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane style={{border: "none"}} key={tabIndex}>
            <CoverageTypeSection
              index={coverageIndex}
              isEdit={isEdit}
              isCustom={insuranceType === "CUSTOM"}
              coverageTypeConfig={insurance_types[insuranceType]}
              coverageRequirement={coverageRequirement}
              onUpdate={onUpdate}
              onDeleteTab={onDeleteTab}
              clientMode={clientMode}
              showHelperText={showHelperText}
              clientCoverage={references[coverageIndex] && references[coverageIndex].clientCoverage}
              classCoverage={references[coverageIndex] && references[coverageIndex].classCoverage}
              vendorCoverage={references[coverageIndex] && references[coverageIndex].vendorCoverage}
            />
          </Tab.Pane>
        )
      }
      if(index === -1) {
        panes.push(pane)
      } else {
        panes[index] = pane
      }
    })
    if(isEdit) {
      panes.push({
        menuItem: (
          <Menu.Item key={panes.length + 1}>
            <Button onClick={handleAddInsuranceType} startIcon={<AddIcon />}>
              Add Insurance Type
            </Button>
          </Menu.Item>
        ),
        render: () => false,
      });
    }
    return panes
  }

  return (
    <>
      <Stack mt={2} className="coverage-description-section">
        <Typography variant="h6" style={{ display: "inline" }}>
          Description of Operation Validation
        </Typography>
        <RadioGroup
          row
          name="descriptionOfOperationValidation"
        >
          <FormControlLabel
            disabled={!isEdit}
            control={<Radio />}
            label="Verbatim Check"
            checked={true}
          />
        </RadioGroup>
        <TextField
          sx={{ 
            mt: 1,
            '& .MuiInputBase-input.Mui-disabled': {
              color: 'rgba(0,0,0,0.6)',
              WebkitTextFillColor: 'rgba(0,0,0,0.6)'
            }
          }}
          fullWidth
          multiline
          rows={8}
          placeholder={"Add description of operation validation ..."}
          name="descriptionOfOperation"
          defaultValue={descriptionOfOperationValidation}
          onBlur={(e)=> setDescriptionOfOperation(e.target.value)}
          disabled={!isEdit}
        />
      </Stack>
      <Tab
        className="coverage-type-tabs"
        panes={getPanes()}
        activeIndex={activeIndex}
        onTabChange={()=>setActiveIndex(undefined)}
        menu={{ secondary: true, pointing: true }}
        />
    </>
  )
})
